import { keyframes } from 'glamor';
import * as React from 'react';
import { Or } from '../../../domain/Or';
import { Curve, Curves } from './Curves';

export class AnimationFactory implements IAnimationData {
	[x: string]: any;

	constructor(
		public name: string,
		public initialState: React.CSSProperties = {},
		public targetCSSProperty: keyof React.CSSProperties = 'opacity',
		public maxValue: number = 1,
		public duration: number = 1000,
		public globalStyles: React.CSSProperties = {},
		public curve: Curve = Curves.Ease(),
		public prerequisiteStyles: React.CSSProperties = {},
		public delay: number = 0,
		public transformer: (value: number) => string = (value) => `${value}`,
		public effects: IAnimationEffects = {
			entered: {},
			exiting: {},
			exited: {},
			entering: {}
		},
		public isOneWay: boolean = false,
		public playInfinitely: boolean = false
	) {
		this.CalculateEffect();
	}

	// Provide a function to transform a curve point value
	// to an appropriate style value For example, when
	// animating the CSS property "transform", provide a method
	// to convert 1.5 to 'scale(1.5)'
	TransformUsing(transformer: (value: number) => string | number) {
		return this.UpdateProperty('transformer', transformer);
	}

	WithInitialState(initialState: React.CSSProperties) {
		return this.UpdateProperty('initialState', initialState);
	}

	WithPrerequisiteStyles(prerequisiteStyles: React.CSSProperties) {
		return this.UpdateProperty('prerequisiteStyles', prerequisiteStyles);
	}

	AfterDelay(delay: number) {
		return this.UpdateProperty('delay', delay);
	}

	WithMaximumValue(maxValue: number) {
		return this.UpdateProperty('maxValue', maxValue);
	}

	OneWay() {
		return this.UpdateProperty('isOneWay', true);
	}

	PlayInfinitely() {
		return this.UpdateProperty('playInfinitely', true);
	}

	UsingCurve(curve: Curve) {
		return this.UpdateProperty('curve', curve);
	}

	TargetingCSSProperty(targetCSSProperty: keyof React.CSSProperties) {
		return this.UpdateProperty('targetCSSProperty', targetCSSProperty);
	}

	ForDuration(duration: number) {
		return this.UpdateProperty('duration', duration);
	}

	CombineWith(other: AnimationFactory) {
		return new CombinedAnimation(this, other);
	}

	private Copy() {
		return new AnimationFactory(
			this.name,
			this.initialState,
			this.targetCSSProperty,
			this.maxValue,
			this.duration,
			this.globalStyles,
			this.curve,
			this.prerequisiteStyles,
			this.delay,
			this.transformer,
			this.effects,
			this.isOneWay,
			this.playInfinitely
		);
	}

	private CalculateEffect() {
		const curveKeyframes = this.curve.GetKeyframes(this.targetCSSProperty, this.maxValue, this.transformer);
		const keyFramesClass = keyframes(`${this.name}-${this.curve.name}_curve-keyFrames`, curveKeyframes);
		const animationDuration = this.isOneWay ? this.duration : this.duration / 2;

		const GetDirectionIterationText = () => {
			if (this.playInfinitely && !this.isOneWay) {
				return `infinite alternate`;
			}

			if (this.playInfinitely && this.isOneWay) {
				return `infinite`;
			}

			if (!this.playInfinitely && !this.isOneWay) {
				return `alternate 2`;
			}

			return `normal 1`;
		};

		const animationStyle: React.CSSProperties = {
			animation: `${keyFramesClass} ${animationDuration}ms ${GetDirectionIterationText()}`,
			animationDelay: `${this.delay}ms`,
			animationFillMode: `forwards`
		};

		this.effects = {
			exited: this.initialState,
			entering: animationStyle,
			entered: this.initialState,
			exiting: animationStyle
		};
	}

	private UpdateProperty<T>(property: string, value: T) {
		var copy = this.Copy();

		if (copy[property] === undefined) {
			throw new Error(`Can't find the property "${property}"`);
		}

		copy[property] = value;
		copy.CalculateEffect();

		return copy;
	}
}

class CombinedAnimation implements IAnimationData {
	readonly name: string = '';

	readonly effects: IAnimationEffects = {
		entered: {},
		exiting: {},
		exited: {},
		entering: {}
	};

	readonly prerequisiteStyles: React.CSSProperties = {};

	readonly delay: number = 0;

	readonly duration: number = 1000;

	readonly playInfinitely: boolean = false;

	constructor(first: IAnimationData, second: IAnimationData) {
		this.name = `${first.name} + ${second.name}`;

		const CombineAnimationStyles = (state: string) => {
			const firstAnimationStyle = Or(first.effects[state][`animation`], '');
			const secondAnimationStyle = Or(second.effects[state][`animation`], '');

			const combinedStyle = `${firstAnimationStyle} ${firstAnimationStyle && secondAnimationStyle
				? ', '
				: ''} ${secondAnimationStyle}`;

			return { animation: combinedStyle };
		};

		this.prerequisiteStyles = { ...first.prerequisiteStyles, ...second.prerequisiteStyles };
		this.duration = Math.max(first.duration, second.duration);
		this.delay = Math.max(first.delay, second.delay);

		this.effects = {
			entered: CombineAnimationStyles(`entered`),
			exiting: CombineAnimationStyles(`exiting`),
			exited: CombineAnimationStyles(`exited`),
			entering: CombineAnimationStyles(`entering`)
		};

		this.playInfinitely = first.playInfinitely || second.playInfinitely;
	}

	CombineWith(other: IAnimationData) {
		return new CombinedAnimation(this, other);
	}
}

export interface IAnimationData {
	name: string;
	effects: IAnimationEffects;
	prerequisiteStyles: React.CSSProperties;
	delay: number;
	duration: number;
	playInfinitely: boolean;
}

export interface IAnimationEffects {
	entered: React.CSSProperties;
	exiting: React.CSSProperties;
	exited: React.CSSProperties;
	entering: React.CSSProperties;
	[x: string]: React.CSSProperties;
}
